// Temporal-cognition primitives (PURE, dependency-free). Maps REAL recency to a visual
// treatment: freshness (warm, brighter, decaying), aging (desaturate + dim + sink), and
// settled (background depth). Spec §5 / Bible §4.
//
// HONESTY: every cue is a function of a real timestamp. "No timed source ⇒ no temporal
// cue" — a node with no real timestamp returns { hasCue:false } and a neutral baseline,
// NEVER a fabricated freshness/aging mark.
//
// Real sources (per node): outcome.created_at, observation.created_at, journal event
// time / entry created_at, moment recency. The caller supplies the timestamp; this
// module only does the recency→treatment math.

export const FRESH_SECONDS = 60 * 60;          // < 1h  : fresh (warm, bright), decays toward baseline
export const AGING_SECONDS = 24 * 60 * 60;     // 1h–24h: aging (cooling, sinking); older => settled

export function recencySeconds(timestamp, now) {
  if (!timestamp) return null;
  const t = timestamp instanceof Date ? timestamp.getTime() : Date.parse(String(timestamp));
  if (Number.isNaN(t)) return null;
  const nowMs = now instanceof Date ? now.getTime() : now;
  const sec = (nowMs - t) / 1000;
  return sec < 0 ? 0 : sec; // clamp future timestamps to "just now"
}

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

// Treatment for a node given its recency in seconds (or null => no cue).
//   luminance : 0..1 brightness multiplier (light = attention/freshness)
//   warmth    : 0..1 warm tint (warm = fresh; cool = aging)   -> 0 means neutral/cool
//   depth     : 0..1 how far the thought has sunk into background (blur/scale/recede)
//   tier      : "fresh" | "aging" | "settled" | "none"
export function temporalTreatment(recencySec) {
  if (recencySec == null) {
    // No real timestamp -> no temporal cue. Neutral baseline (present, unmarked).
    return { tier: "none", luminance: 0.8, warmth: 0, depth: 0, hasCue: false };
  }
  if (recencySec < FRESH_SECONDS) {
    // Fresh: warm + bright, decaying toward baseline as it ages within the hour.
    const t = clamp01(recencySec / FRESH_SECONDS); // 0 (just now) -> 1 (1h old)
    return {
      tier: "fresh",
      luminance: +(1 - 0.15 * t).toFixed(3), // 1.0 -> 0.85
      warmth: +(1 - t).toFixed(3),           // 1.0 -> 0.0
      depth: 0,
      hasCue: true,
    };
  }
  if (recencySec < AGING_SECONDS) {
    // Aging: cools, dims, sinks slightly into depth.
    const t = clamp01((recencySec - FRESH_SECONDS) / (AGING_SECONDS - FRESH_SECONDS)); // 0..1
    return {
      tier: "aging",
      luminance: +(0.85 - 0.3 * t).toFixed(3), // 0.85 -> 0.55
      warmth: 0,
      depth: +(0.4 * t).toFixed(3),            // 0 -> 0.4
      hasCue: true,
    };
  }
  // Settled / old: quiet, dim, receded into background depth.
  return { tier: "settled", luminance: 0.5, warmth: 0, depth: 0.6, hasCue: true };
}

// Convenience: treatment straight from a timestamp + now.
export function treatmentFor(timestamp, now) {
  return temporalTreatment(recencySeconds(timestamp, now));
}
